"use client";

import { CheckCircle, XCircle, Repeat } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import { useImmatriculationFlowsQuery } from "@/lib/queries/hrin/immat-flow";
import { useToggleImmatriculationFlowMutation } from "@/lib/queries/hrin/use-toggle-immatriculation-flow";
import type { FlowType } from "@/lib/schemas/hrin/immat-flows";

import { Skeleton } from "@/components/ui/skeleton";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { NoQuery } from "../../no-query";

export default function ImmatriculationFlow() {
  const queryClient = useQueryClient();

  const { data, error, isLoading } = useImmatriculationFlowsQuery();

  const toggleFlowMutation = useToggleImmatriculationFlowMutation();

  function toggleFlow(flowType: FlowType, isActive: boolean) {
    toggleFlowMutation.mutate(
      { flowType, isActive },
      {
        onSuccess: () => {
          queryClient.invalidateQueries();
          toast.success("Flux mis à jour avec succès");
        },

        onError: (err) => {
          toast.error(err?.message || "Impossible de modifier le flux");
        },
      },
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-100 w-full rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Empty className="border border-dashed text-red-600">
            <EmptyHeader>
              <EmptyMedia variant="icon" className="text-red-600">
                <XCircle />
              </EmptyMedia>
              <EmptyTitle>Erreur</EmptyTitle>
              <EmptyDescription className="text-red-600">
                Erreur chargement flux
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </CardContent>
      </Card>
    );
  }

  if (!data?.immatriculationFlows?.length) {
    return (
      <NoQuery title="Gestion des flux" description="Aucun flux disponible" />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Repeat className="text-primary size-5" />
          {"Gestion des flux d’immatriculation"}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <div className="space-y-3">
          {data.immatriculationFlows.map((flow) => (
            <div
              key={flow.flowType}
              className="flex items-center justify-between rounded-lg border p-3"
            >
              <div className="flex items-center gap-2">
                {flow.isActive ? (
                  <CheckCircle className="text-green-500" />
                ) : (
                  <XCircle className="text-red-500" />
                )}

                <span className="font-medium">{flow.flowType}</span>
              </div>

              <Switch
                checked={flow.isActive}
                onCheckedChange={(v) => toggleFlow(flow.flowType, v)}
                disabled={toggleFlowMutation.isPending}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
